'use client';

import type { Theme, SxProps } from '@mui/material/styles';
import type { SyntheticEvent, ImgHTMLAttributes } from 'react';

import { getImageProps } from 'next/image';
import { useState, useCallback } from 'react';
import { varAlpha } from 'minimal-shared/utils';

import Box from '@mui/material/Box';
import { keyframes } from '@mui/material/styles';

const SHIMMER = keyframes`
  from { background-position: 150% 0; }
  to { background-position: -50% 0; }
`;

/**
 * 그림을 받는 동안 칸에 까는 반짝임 — `LoadingImg` 밖에서 같은 모양이 필요할 때(배경 그림 칸) 쓴다.
 */
export function imageLoadingSx(theme: Theme) {
  const tint = varAlpha(theme.vars.palette.grey['500Channel'], 0.12);
  const glint = varAlpha(theme.vars.palette.grey['500Channel'], 0.14);
  return {
    backgroundColor: tint,
    backgroundImage: `linear-gradient(90deg, transparent 0%, ${glint} 50%, transparent 100%)`,
    backgroundSize: '200% 100%',
    backgroundRepeat: 'no-repeat',
    animation: `${SHIMMER} 1.4s ease-in-out infinite`,
    '@media (prefers-reduced-motion: reduce)': { animation: 'none', backgroundImage: 'none' },
  };
}

/**
 * 실물 그림 한 장 — **받는 동안 제 칸에 옅은 반짝임(스켈레톤)을 깔고**, 다 받으면 걷는다.
 *
 * 🔑 반짝임은 `<img>` 자신의 배경이다 — 감싸는 상자를 더하지 않으니 칸 크기·자리가 그대로다(레이아웃 밀림 없음).
 * 🔑 다 받으면 배경을 걷는다 — `contain` 으로 넣은 그림의 남는 자리에 반짝임이 남지 않게.
 * ⚠️ 깨진 그림의 대체 표시(「미리보기 없음」·글자 얼굴)는 부르는 쪽 몫이다 — `onError` 로 알리기만 한다.
 * ⚠️ 움직임 줄이기(`prefers-reduced-motion`)면 반짝이지 않고 옅은 바탕만 남는다.
 * 🔑 `displayWidth`(화면에 보이는 너비 px)를 주면 우리 저장소 그림(`OPTIMIZABLE`)은 그 너비로 줄여 받는다(Next 이미지 최적화 ·
 *   1x·2x). 작은 칸에 원본(수백 KB~1MB대)을 받지 않게 — 안 주거나 다른 호스트면 원본 그대로다.
 *   ⚠️ 줄인 그림은 Vercel 이미지 최적화로 과금된다(변환·캐시) — 칸 크기가 정해진 자리에만 준다.
 */
export function LoadingImg({
  src,
  alt = '',
  sx,
  displayWidth,
  onLoad,
  onError,
  ...other
}: Omit<ImgHTMLAttributes<HTMLImageElement>, 'src' | 'onLoad' | 'onError'> & {
  src: string;
  sx?: SxProps<Theme>;
  /** 화면에 보이는 너비(px) — 주면 우리 저장소 그림을 그 너비로 줄여 받는다 */
  displayWidth?: number;
  onLoad?: (event: SyntheticEvent<HTMLImageElement>) => void;
  onError?: (event: SyntheticEvent<HTMLImageElement>) => void;
}) {
  const [loadedSrc, setLoadedSrc] = useState<string | null>(null);
  const loaded = loadedSrc === src;
  const sized = displayWidth ? sizedSource(src, displayWidth) : null;

  // 하이드레이션 전에 캐시에서 이미 받은 그림은 load 가 다시 안 온다 — 붙는 순간 한 번 본다
  const probeRef = useCallback(
    (img: HTMLImageElement | null) => {
      if (img?.complete && img.naturalWidth > 0) setLoadedSrc(src);
    },
    [src]
  );

  return (
    <Box
      component="img"
      ref={probeRef}
      src={sized?.src ?? src}
      srcSet={sized?.srcSet}
      alt={alt}
      onLoad={(event: SyntheticEvent<HTMLImageElement>) => {
        setLoadedSrc(src);
        onLoad?.(event);
      }}
      onError={onError}
      sx={[
        ...(loaded ? [] : [imageLoadingSx]),
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
      {...other}
    />
  );
}

// ---- 이하 비공개 ----------------------------------------------------------

/** 줄여 받을 수 있는 우리 저장소 — 포스트·상품 버킷과 Firebase Storage */
const OPTIMIZABLE = [
  'https://storage.googleapis.com/catchmoments-images/',
  'https://firebasestorage.googleapis.com/',
];
const SIZED_QUALITY = 75;

/** 우리 저장소 그림이면 `width` 로 줄인 주소(1x·2x)를 — 아니면 null(원본을 쓴다) */
function sizedSource(src: string, width: number): { src: string; srcSet?: string } | null {
  if (!OPTIMIZABLE.some((prefix) => src.startsWith(prefix))) return null;
  const { props } = getImageProps({ src, alt: '', width, height: width, quality: SIZED_QUALITY });
  return { src: props.src, srcSet: props.srcSet };
}

